import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const EmailSenderSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [emailSenders, setEmailSenders] = useState({
    auth: { email: "", displayName: "" },
    bookings: { email: "", displayName: "" },
    orders: { email: "", displayName: "" },
    reviews: { email: "", displayName: "" },
    team: { email: "", displayName: "" },
    packages: { email: "", displayName: "" },
    billings: { email: "", displayName: "" },
    welcome: { email: "", displayName: "" },
    alerts: { email: "", displayName: "" },
    marketing: { email: "", displayName: "" },
    noreply: { email: "", displayName: "" },
  });

  useEffect(() => {
    checkPortalAdmin();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPortalAdmin = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return;
      }
    } catch (error) {
      navigate("/login");
    }
  };

  const fetchSettings = async () => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/platform-settings`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (
        response.data &&
        response.data.settings &&
        response.data.settings.emailSenders
      ) {
        const senders = response.data.settings.emailSenders;
        // Map settings to component state, handling both new and legacy formats
        const mapped = {};
        Object.keys(senders).forEach((key) => {
          const sender = senders[key];
          if (typeof sender === "string") {
            // Legacy format: just email
            mapped[key] = { email: sender, displayName: "" };
          } else if (typeof sender === "object" && sender !== null) {
            // New format: object with email and displayName
            mapped[key] = {
              email: sender.email || "",
              displayName: sender.displayName || "",
            };
          } else {
            mapped[key] = { email: "", displayName: "" };
          }
        });
        setEmailSenders(mapped);
      }
      setMessage({ type: "", text: "" });
    } catch (err) {
      setMessage({
        type: "error",
        text: "Failed to load email sender settings",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, field, value) => {
    setEmailSenders((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });

    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/platform-settings`,
        { emailSenders },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMessage({
        type: "success",
        text: "Email sender settings updated successfully!",
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to update settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const emailCategories = [
    {
      key: "auth",
      icon: "🔐",
      label: "Authentication",
      description: "Verification emails, login OTPs, password resets",
    },
    {
      key: "bookings",
      icon: "📅",
      label: "Bookings",
      description: "Confirmations, reminders, cancellations",
    },
    {
      key: "orders",
      icon: "🛒",
      label: "Orders",
      description: "Order confirmations and notifications",
    },
    {
      key: "reviews",
      icon: "⭐",
      label: "Reviews",
      description: "Review requests and responses",
    },
    {
      key: "team",
      icon: "👥",
      label: "Team",
      description: "Staff invitations and management",
    },
    {
      key: "packages",
      icon: "📦",
      label: "Packages",
      description: "Package purchase confirmations",
    },
    {
      key: "billings",
      icon: "💳",
      label: "Billing",
      description: "Payments, payouts, subscriptions",
    },
    {
      key: "welcome",
      icon: "👋",
      label: "Welcome",
      description: "Onboarding and welcome emails",
    },
    {
      key: "alerts",
      icon: "🔔",
      label: "Alerts",
      description: "System alerts and notifications",
    },
    {
      key: "marketing",
      icon: "📢",
      label: "Marketing",
      description: "Campaigns and newsletters",
    },
    {
      key: "noreply",
      icon: "✉️",
      label: "No-Reply",
      description: "System-generated emails",
    },
  ];

  if (loading) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p style={{ marginTop: "20px", color: "#6b7280" }}>
            Loading email sender settings...
          </p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div style={{ padding: "32px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "8px",
            }}
          >
            📧 Email Sender Configuration
          </h1>
          <p style={{ fontSize: "15px", color: "#6b7280", lineHeight: "1.6" }}>
            Configure sender addresses for different types of emails. You can
            enter a full email address or just the prefix, and the app will
            append your Mailgun domain automatically.
          </p>
        </div>

        {/* Alert Messages */}
        {message.text && (
          <div
            style={{
              padding: "16px 20px",
              borderRadius: "8px",
              marginBottom: "24px",
              backgroundColor:
                message.type === "success" ? "#d1fae5" : "#fee2e2",
              border: `1px solid ${message.type === "success" ? "#86efac" : "#fca5a5"}`,
              color: message.type === "success" ? "#065f46" : "#991b1b",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <span style={{ fontSize: "20px" }}>
              {message.type === "success" ? "✓" : "⚠"}
            </span>
            <span style={{ flex: 1 }}>{message.text}</span>
            <button
              onClick={() => setMessage({ type: "", text: "" })}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "inherit",
                opacity: 0.6,
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Email Senders Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
              gap: "20px",
              marginBottom: "32px",
            }}
          >
            {emailCategories.map((category) => (
              <div
                key={category.key}
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "24px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0, 0, 0, 0.08)";
                  e.currentTarget.style.borderColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <span style={{ fontSize: "28px" }}>{category.icon}</span>
                  <div style={{ flex: 1 }}>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1a1a1a",
                        margin: "0 0 4px 0",
                      }}
                    >
                      {category.label}
                    </h3>
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                        margin: 0,
                        lineHeight: "1.4",
                      }}
                    >
                      {category.description}
                    </p>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor={`${category.key}-email`}
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "6px",
                    }}
                  >
                    Sender Email (prefix or full address)
                  </label>
                  <input
                    type="text"
                    id={`${category.key}-email`}
                    value={emailSenders[category.key]?.email || ""}
                    onChange={(e) =>
                      handleChange(category.key, "email", e.target.value)
                    }
                    placeholder={`${category.key} or ${category.key}@mg.ogestyle.com`}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "14px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      transition: "all 0.2s",
                      marginBottom: "12px",
                    }}
                  />
                  <label
                    htmlFor={`${category.key}-displayName`}
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "6px",
                    }}
                  >
                    Display Name (shown in From field)
                  </label>
                  <input
                    type="text"
                    id={`${category.key}-displayName`}
                    value={emailSenders[category.key]?.displayName || ""}
                    onChange={(e) =>
                      handleChange(category.key, "displayName", e.target.value)
                    }
                    placeholder={`e.g., OGESTYLE ${category.label}`}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "14px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      transition: "all 0.2s",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Info Box */}
          <div
            style={{
              background: "#fef3c7",
              border: "1px solid #fbbf24",
              borderRadius: "12px",
              padding: "20px 24px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: "#92400e",
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>ℹ️</span> How to Use
            </h3>
            <ul
              style={{
                margin: 0,
                paddingLeft: "20px",
                color: "#78350f",
                fontSize: "14px",
                lineHeight: "1.8",
              }}
            >
              <li>
                <strong>Sender Email:</strong> Enter a prefix (e.g., "crm") or full
                email address (e.g., "crm@mg.ogestyle.com")
              </li>
              <li>
                <strong>Display Name:</strong> The name that appears in the "From"
                field of emails (e.g., "OGESTYLE Bookings")
              </li>
              <li>Prefix-only values are expanded with your Mailgun domain automatically</li>
              <li>All sender values must be accepted by Mailgun</li>
              <li>Changes take effect immediately for new emails</li>
              <li>Test emails after making changes to ensure deliverability</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              paddingTop: "8px",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/portal-admin")}
              style={{
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: "500",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#374151",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#f9fafb";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "white";
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "12px 32px",
                fontSize: "15px",
                fontWeight: "500",
                border: "none",
                borderRadius: "8px",
                background: saving ? "#9ca3af" : "#3b82f6",
                color: "white",
                cursor: saving ? "not-allowed" : "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!saving) e.target.style.background = "#2563eb";
              }}
              onMouseLeave={(e) => {
                if (!saving) e.target.style.background = "#3b82f6";
              }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </PortalAdminLayout>
  );
};

export default EmailSenderSettings;
